import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2CrmRecordInternalNotesResponse,
  AdminV2CrmUserInternalNoteResponse,
} from "@/types/adminV2";

/** The records that keep a notes list (Record pages row 11). */
export type AdminV2CrmRecordNoteKind = "organisation" | "firm" | "person";

const RECORD_NOTE_COLLECTION: Readonly<
  Record<AdminV2CrmRecordNoteKind, string>
> = {
  organisation: "organisations",
  firm: "firms",
  person: "people",
};

const notesUrl = (kind: AdminV2CrmRecordNoteKind, recordId: string) =>
  `/admin/v2/crm/${RECORD_NOTE_COLLECTION[kind]}/${encodeURIComponent(recordId)}/internal-notes`;

/**
 * Notes on organisation, partner (firm) and contact records.
 *
 * @remarks
 * The same shape as the user notes endpoints. Adding a note also writes an
 * Activity row on the server, so every CRM timeline refetches.
 */
export const buildAdminV2CrmRecordNoteEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2CrmRecordInternalNotes: builder.query<
    AdminV2CrmRecordInternalNotesResponse,
    { kind: AdminV2CrmRecordNoteKind; recordId: string; limit?: number }
  >({
    query: ({ kind, recordId, limit }) => ({
      url: notesUrl(kind, recordId),
      params: limit ? { limit } : {},
    }),
    providesTags: (_result, _error, { kind, recordId }) => [
      { type: "AdminV2CrmRecordInternalNotes", id: `${kind}:${recordId}` },
    ],
    keepUnusedDataFor: 10,
  }),

  createAdminV2CrmRecordInternalNote: builder.mutation<
    AdminV2CrmUserInternalNoteResponse,
    {
      kind: AdminV2CrmRecordNoteKind;
      recordId: string;
      note: string;
      /**
       * One UUID v4 per draft, reused on retry: the API returns the existing
       * note for the same author, record and id instead of a duplicate.
       */
      clientRequestId?: string;
    }
  >({
    query: ({ kind, recordId, note, clientRequestId }) => ({
      url: notesUrl(kind, recordId),
      method: "POST",
      data: { note, ...(clientRequestId ? { clientRequestId } : {}) },
    }),
    invalidatesTags: (_result, _error, { kind, recordId }) => [
      { type: "AdminV2CrmRecordInternalNotes", id: `${kind}:${recordId}` },
      "AdminV2CrmTimeline",
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
